<template>
  <div ref="rootRef" class="relative">
    <label v-if="label" class="form-label" :for="inputId">
      {{ label }}<span v-if="required" class="text-red-500"> *</span>
    </label>

    <div
      class="flex min-h-[42px] w-full items-stretch overflow-hidden border bg-white shadow-sm transition-colors"
      :class="[
        open ? 'border-indigo-400 ring-3 ring-indigo-100' : 'border-gray-200',
        errors.length ? 'border-red-500 ring-red-200' : '',
      ]"
    >
      <button
        :id="inputId"
        type="button"
        class="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left outline-none focus:outline-none focus-visible:outline-none"
        :aria-expanded="open"
        aria-haspopup="listbox"
        @click="toggleOpen"
      >
        <template v-if="modelValue">
          <span class="truncate font-medium text-gray-900">
            {{ displayLabel }}
          </span>
          <span
            v-if="displayTypeLabel"
            class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
          >
            {{ displayTypeLabel }}
          </span>
        </template>
        <span v-else class="text-gray-500">
          {{ placeholder }}
        </span>
      </button>

      <button
        v-if="modelValue"
        type="button"
        class="action-btn text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Wyczyść wybór"
        @click.stop="clear"
      >
        <svg class="h-6 w-6" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
          <path stroke-linecap="round" d="M6 6l8 8M14 6l-8 8" />
        </svg>
      </button>

      <button
        type="button"
        class="action-btn text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Otwórz listę"
        @click="toggleOpen"
      >
        <svg
          class="h-6 w-6 transition-transform duration-200"
          :class="{ 'rotate-180': open }"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        class="z-[10050] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        :style="panelStyle"
        role="listbox"
      >
        <div class="border-b border-gray-100 p-2">
          <input
            ref="searchRef"
            v-model="query"
            type="search"
            class="form-input w-full text-sm"
            :placeholder="searchPlaceholder"
            autocomplete="off"
            @keydown.down.prevent="moveHighlight(1)"
            @keydown.up.prevent="moveHighlight(-1)"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.escape.prevent="close"
          >
        </div>

        <ul class="max-h-64 overflow-y-auto py-1">
          <li v-if="loading" class="px-4 py-6 text-center text-sm text-gray-500">
            Szukam…
          </li>
          <li v-else-if="!results.length" class="px-4 py-6 text-center text-sm text-gray-500">
            {{ query.trim() ? `Brak wyników dla „${query.trim()}”` : 'Brak wyników' }}
          </li>
          <li
            v-for="(result, index) in results"
            :key="`${result.type}-${result.url}`"
            role="option"
            :aria-selected="result.url === modelValue"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors"
              :class="index === highlightedIndex ? 'bg-indigo-50' : 'hover:bg-gray-50'"
              @click="selectResult(result)"
              @mouseenter="highlightedIndex = index"
            >
              <span class="min-w-0 flex-1">
                <span class="font-medium text-gray-900">{{ result.label }}</span>
              </span>
              <span class="shrink-0 text-xs text-gray-500">{{ result.typeLabel }}</span>
              <svg
                v-if="result.url === modelValue"
                class="h-5 w-5 shrink-0 text-indigo-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
              </svg>
            </button>
          </li>
        </ul>
      </div>
    </teleport>

    <div v-if="errors.length" class="form-error">
      {{ errors[0] }}
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { v4 as uuid } from 'uuid';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  linkLabel: {
    type: String,
    default: '',
  },
  typeLabel: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Wybierz specjalistę, specjalizację lub usługę…',
  },
  required: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Array,
    default: () => [],
  },
  inputId: {
    type: String,
    default: () => `resource-link-select-${uuid()}`,
  },
  includeArticles: {
    type: Boolean,
    default: false,
  },
  includePages: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:linkLabel', 'update:typeLabel']);

const rootRef = ref(null);
const panelRef = ref(null);
const searchRef = ref(null);
const open = ref(false);
const query = ref('');
const results = ref([]);
const loading = ref(false);
const highlightedIndex = ref(0);
const panelStyle = ref({ position: 'fixed', left: '0', top: '0', width: '0' });

let cleanupFloating = null;
let debounceTimer = null;
let requestToken = 0;

const displayLabel = computed(() => {
  if (props.linkLabel) return props.linkLabel;
  if (!props.modelValue) return '';
  try {
    return props.modelValue.replace(/^https?:\/\/[^/]+/, '') || props.modelValue;
  } catch {
    return props.modelValue;
  }
});

const displayTypeLabel = computed(() => props.typeLabel || '');

const searchPlaceholder = computed(() => {
  if (props.includeArticles && props.includePages) {
    return 'Szukaj specjalisty, specjalizacji, usługi, aktualności lub strony…';
  }
  if (props.includeArticles) {
    return 'Szukaj specjalisty, specjalizacji, usługi lub aktualności…';
  }
  if (props.includePages) {
    return 'Szukaj specjalisty, specjalizacji, usługi lub strony…';
  }
  return 'Szukaj specjalisty, specjalizacji lub usługi…';
});

watch(results, () => {
  highlightedIndex.value = 0;
});

watch(open, async (isOpen) => {
  if (isOpen) {
    query.value = '';
    highlightedIndex.value = Math.max(
      0,
      results.value.findIndex((result) => result.url === props.modelValue),
    );
    await nextTick();
    positionPanel();
    searchRef.value?.focus();
    runSearch();
    if (rootRef.value && panelRef.value) {
      cleanupFloating = autoUpdate(rootRef.value, panelRef.value, positionPanel);
    }
  } else if (cleanupFloating) {
    cleanupFloating();
    cleanupFloating = null;
  }
});

watch(query, () => {
  if (!open.value) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runSearch, 200);
});

async function positionPanel() {
  if (!rootRef.value || !panelRef.value) return;

  const { x, y } = await computePosition(rootRef.value, panelRef.value, {
    placement: 'bottom-start',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
  });

  panelStyle.value = {
    position: 'fixed',
    left: `${x}px`,
    top: `${y}px`,
    width: `${rootRef.value.offsetWidth}px`,
    zIndex: 10050,
  };
}

async function runSearch() {
  const term = query.value.trim();
  const token = ++requestToken;
  loading.value = true;
  try {
    const params = new URLSearchParams({ q: term });
    if (props.includeArticles) params.set('include_articles', '1');
    if (props.includePages) params.set('include_pages', '1');

    const response = await fetch(`/api/search?${params}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    if (token !== requestToken) return;
    results.value = Array.isArray(payload.results) ? payload.results : [];
  } catch {
    if (token !== requestToken) return;
    results.value = [];
  } finally {
    if (token === requestToken) loading.value = false;
  }
}

function toggleOpen() {
  open.value = !open.value;
}

function close() {
  open.value = false;
}

function clear() {
  emit('update:modelValue', '');
  emit('update:linkLabel', '');
  emit('update:typeLabel', '');
  close();
}

function selectResult(result) {
  emit('update:modelValue', result.url);
  emit('update:linkLabel', result.label);
  emit('update:typeLabel', result.typeLabel);
  close();
}

function selectHighlighted() {
  const result = results.value[highlightedIndex.value];
  if (result) selectResult(result);
}

function moveHighlight(delta) {
  const len = results.value.length;
  if (len === 0) return;
  highlightedIndex.value = (highlightedIndex.value + delta + len) % len;
}

function onDocumentClick(event) {
  if (!open.value) return;
  const root = rootRef.value;
  const panel = panelRef.value;
  if (root?.contains(event.target) || panel?.contains(event.target)) return;
  close();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  if (cleanupFloating) cleanupFloating();
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<style scoped>
.action-btn {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 3rem;
  align-self: stretch;
  border-left: 1px solid #e5e7eb;
  border-radius: 0;
  outline: none;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.action-btn:focus,
.action-btn:focus-visible {
  outline: none;
  box-shadow: none;
}
</style>
